import { useController, useFormContext, useWatch } from 'react-hook-form';

import {
	type CosmeticReportVdpConfig,
	cosmeticReportVdpConfigSchema,
	Services,
	type ServicesProvider,
} from '@ekhodealer/ekho-common';
import { Box, Form, Stack, TextInput } from '@ekhodealer/ekho-common/rev';

import { ComingSoonOverlay } from '@common/components/ComingSoonOverlay';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateDomainsVdpConfig } from '../../api/useUpdateDomainsVdpConfig';

function getDefaultValues(servicesProvider: ServicesProvider | undefined): CosmeticReportVdpConfig {
	const config = servicesProvider?.config?.[Services.DOMAINS]?.carthage?.cosmeticInspection;

	return {
		enabled: config?.enabled ?? false,
		processSummary: config?.processSummary,
		processDetails: config?.processDetails,
		processVideoUrl: config?.processVideoUrl,
	};
}

function CosmeticReportFields(): React.JSX.Element {
	const enabled = useWatch({ name: 'enabled' });
	const { trigger } = useFormContext<CosmeticReportVdpConfig>();
	const { field: videoUrlField } = useController({ name: 'processVideoUrl' });

	return (
		<Stack itemsSpacing='16'>
			<Form.Field.Text
				schemaField='processSummary'
				labelText='Process summary'
				placeholder='Brief summary of the report process'
				isDisabled={!enabled}
			/>
			<Form.Field.TextArea
				schemaField='processDetails'
				labelText='Process details'
				placeholder='Detailed description of the report process'
				isDisabled={!enabled}
			/>
			<Form.Item
				schemaField='processVideoUrl'
				labelText='Process video URL'
				isDisabled={!enabled}
				skipAdapterProps={['onBlur']}>
				<TextInput
					placeholder='https://'
					isDisabled={!enabled}
					onBlur={() => {
						videoUrlField.onBlur();
						void trigger('processVideoUrl');
					}}
				/>
			</Form.Item>
		</Stack>
	);
}

export function CosmeticReportConfig(): React.JSX.Element {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const { mutateAsync: updateVdpConfig } = useUpdateDomainsVdpConfig();

	const handleSubmit = async (data: CosmeticReportVdpConfig): Promise<{ isSuccess: boolean }> => {
		try {
			await updateVdpConfig({ cosmeticInspection: data });
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<ComingSoonOverlay>
			<Box padding='24' background='secondary' rounding='12'>
				<Form
					name='cosmetic-report-config'
					schema={cosmeticReportVdpConfigSchema}
					defaultValues={getDefaultValues(servicesProvider)}
					onSubmit={handleSubmit}
					title='Cosmetic report'
					titleLevel='subgroup'
					titleWeight='semibold'
					toggle={{ fieldName: 'enabled' }}
					showSaveAndClear>
					<CosmeticReportFields />
				</Form>
			</Box>
		</ComingSoonOverlay>
	);
}
